import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register: doRegister, formatError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "parent" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await doRegister(form);
      toast.success("Account created. Welcome!");
      const dashDest =
        u.role === "parent" ? "/parent" :
        u.role === "student" ? "/student" :
        (u.role === "cleaner" || u.role === "service_provider") ? "/cleaner" :
        "/admin";
      navigate(dashDest, { replace: true });
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12 grid-bg">
      <Card className="w-full max-w-md border-border/60">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground"><Sparkles size={16} /></div>
            <div className="font-display font-bold text-primary">QuadScrub</div>
          </div>
          <h1 className="font-display text-3xl font-bold text-primary">Create your account</h1>
          <p className="text-sm text-muted-foreground mt-1">Parents start here. Students join via parent invite.</p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
              <Input required value={form.name} onChange={(e) => up("name", e.target.value)} data-testid="reg-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => up("email", e.target.value)} data-testid="reg-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone (optional)</Label>
              <Input value={form.phone} onChange={(e) => up("phone", e.target.value)} data-testid="reg-phone" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => up("password", e.target.value)} data-testid="reg-password" />
            </div>
            {err && <div className="text-sm text-destructive">{err}</div>}
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={busy} data-testid="reg-submit">
              {busy ? "Creating..." : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-center text-muted-foreground">
            Already a member? <Link to="/login" className="text-secondary font-semibold" data-testid="goto-login">Sign in</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
